<template>
  <div class="page page-data-management">
    <div class="page-toolbar bg-white shadow-sm p-3">
      <breadcrumb />
    </div>
    <div
      id="pageContent"
      class="page-content d-flex flex-column p-3"
    >
      <content-spinner
        :show="table.loading"
        text="Please wait..."
      />
      <div
        v-show="!table.loading"
        class="complex-table-wrapper"
      >
        <complex-table-view
          v-if="!table.loading"
          ref="table"
          :small="true"
          :items="items"
          :fields="tableFields"
          :all-fields="sortedFields"
          :total-rows="items.length"
          :is-history-view="true"
          :filters="filters"
          :modal-name="'history'"
          selected="all"
          @sort-changed="onSortChanged"
          @filter="onFilterChanged"
          @on-export="exportData"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../../../core/components/ContentSpinner';
import Breadcrumb from '../../../../core/components/Breadcrumb';
import filterMixin from '../../../../core/mixins/filter.mixin';
import moment from 'moment';
import ComplexTableView from '../../../../core/components/complex-table/components/ComplexTableView';
import {getHistoryTable} from '../../../../store/modules/data-management/actions';
import * as XLSX from 'sheetjs-style';
import * as FileSaver from 'file-saver';

const {mapState, mapActions, mapGetters} = createNamespacedHelpers('dataManagement');
export default {
  name: 'HistoryTableView',
  components: {ComplexTableView, ContentSpinner, Breadcrumb},
  mixins: [filterMixin],
  data: () => {
    return {
      sortBy: 'age',
      sortDesc: false,
      items: [],
      localFields: [],
      readOnly: false,
      filters: {},
      sortedParams: {
        sortBy: null,
        sortDesc: null,
      },
    };
  },
  computed: {
    ...mapState(['table', 'permissionTypes', 'validation']),
    ...mapGetters(['fields']),
    ...mapState({
      permissionItemTypes: state => state.permissionItemTypes,
    }),
    tableFields() {
      return this.sortedFields.filter(f => {
        if (f.key === '__IsDeleted__') {
          return true;
        }
        if ([this.permissionTypes.none, this.permissionTypes.deny].includes(f.currentUserPermissionLevel)) {
          return false;
        }

        const show = f.displayInTable && !f.isPrimaryKey;

        if (!this.readOnly) {
          //show = show && !(f.isInternal && f.dataTypeName === 'createdDate')
          if (f.dataTypeName === 'createdDate') {
            f.displayName = 'Created Date';
          }
          if (f.dataTypeName === 'lastUpdatedDate') {
            f.displayName = 'Last Update Date';
          }
          if (f.dataTypeName === 'lastUpdatedBy') {
            f.displayName = 'Updated By';
          }
        }

        return show;
      });
    },
    sortedFields() {
      const internalFields = [];
      const fields = this.localFields.reduce((fields, field) => (field.isInternal ? internalFields.push(field) : fields.push(field), fields), []);
      return [...fields, ...internalFields, {
        key: '__IsDeleted__',
        name: '__IsDeleted__',
        label: 'Is Deleted',
        tdClass: 'px-2 align-middle text-center pr-4',
        thStyle: {width: '150px'},
        dataTypeName: 'boolean',
        thClass: 'text-center pr-4',
      }];
    },
    breadCrumb() {
      return [
        {
          text: this.table.schema.folder.module.moduleName,
          to: {
            name: 'dataManagementModuleView',
            params: {
              moduleUuid: this.table.schema.folder.moduleGuid,
            },
          },
        },
        {
          text: this.table.schema.folder.folderName,
          active: true,
        },
        {
          text: this.table.schema.displayName,
          to: {
            name: 'dataManagementTableView',
            params: {
              tableId: this.$route.params.tableId,
            },
          },
        },
      ];
    },
  },
  methods: {
    ...mapActions(['getHistoryTable']),
    onSortChanged(e) {
      this.items.sort(function (a, b) {
        if (a[e.sortBy] < b[e.sortBy]) {
          return e.sortDesc ? 1 : -1;
        }
        if (a[e.sortBy] > b[e.sortBy]) {
          return e.sortDesc ? -1 : 1;
        }
        return 0;
      });
    },
    isFieldDatetime(field) {
      return [this.fieldValues.dateTime, this.fieldValues.createdDate, this.fieldValues.lastUpdatedDate].includes(field.dataTypeName);
    },
    isFieldInternal(field) {
      return [this.fieldValues.lastUpdatedBy].includes(field.dataTypeName);
    },
    onFilterChanged(filters) {
      const filter = filters.filter || filters;
      this.items = this.table.data.filter((item) => {
        for (const filterFieldName in filter) {
          if (!filter.hasOwnProperty(filterFieldName)) {
            continue;
          }
          const field = this.tableFields.find((f) => f.name === filterFieldName);
          if (filterFieldName === 'recordStatus') {
            if (!this.statusFilter(filter[filterFieldName], item[this.validation.status])) {
              return false;
            }
          } else {
            if (field.dataTypeName === this.fieldValues.text && !this.textFilter(filter[filterFieldName], item[filterFieldName])) {
              return false;
            } else if ([this.fieldValues.wholeNumber, this.fieldValues.decimalNumber, this.fieldValues.floatNumber].includes(field.dataTypeName) && !this.numberFilter(filter[filterFieldName], item[filterFieldName])) {
              return false;
            } else if (field.dataTypeName === this.fieldValues.boolean && !this.booleanFilter(filter[filterFieldName], item[filterFieldName])) {
              return false;
            } else if (field.dataTypeName === this.fieldValues.lookup && !this.lookupFilter(filter[filterFieldName], item[filterFieldName])) {
              return false;
            } else if (field.dataTypeName === this.fieldValues.parentChildHierarchy && !this.hierarchyFilter(filter[filterFieldName], item[filterFieldName], field)) {
              return false;
            } else if (this.isFieldDatetime(field) && !this.dateFilter(filter[filterFieldName], item[filterFieldName])) {
              return false;
            } else if (this.isFieldInternal(field) && !this.internalFieldFilter(filter[filterFieldName], item[filterFieldName])) {
              return false;
            }
          }
        }
        return true;
      });
    },
    getFiltersFromUrl() {
      if (this.$route.query.filters) {
        return JSON.parse(decodeURIComponent(this.$route.query.filters));
      }
      return {};
    },
    filterByPrimaryKey(filter) {
      const fieldName = this.table.schema.fields.filter(f => f.isPrimaryKey)[0].name;
      this.items = this.items.filter((item) => {
        return filter[fieldName].includes(item[fieldName]);
      });
    },
    hierarchyFilter(filteredField, uploadField, field) {
      return filteredField.selectItems.findIndex((elem) => elem[field.data.options.hierarchyField.primary.name] === uploadField) > -1;
    },
    lookupFilter(filteredField, uploadField) {
      return filteredField.selectItems.findIndex((elem) => elem.id === uploadField) > -1;
    },
    internalFieldFilter(filteredField, uploadField) {
      return this.textFilter(filteredField, uploadField);
    },
    async getData() {
      const params = {
        id: this.$route.params.tableId,
        limit: this.table.pagination.perPage,
        offset: (this.table.pagination.currentPage - 1) * this.table.pagination.perPage,
        page: this.$refs.table.currentPage,
      };
      await this.getHistoryTable(params);
      this.localFields = JSON.parse(JSON.stringify(this.fields));
      this.items = this.sortedTableData();
      this.filters = this.getFiltersFromUrl();
      if (this.sortedParams.sortBy !== null) {
        this.onSortChanged(this.sortedParams.sortBy, this.sortedParams.sortDesc);
      }
      if (Object.keys(this.filters).length > 0) {
        await this.getHistoryTable(params);
        this.onFilterChanged(this.filters);
      }
    },
    sortedTableData() {
      const primaryField = this.localFields.find(f => f.dataTypeName === this.fieldValues.primaryKey);
      return this.table.data.sort(function (a, b) {
        return a[primaryField.name] < b[primaryField.name] ? -1 : a[primaryField.name] > b[primaryField.name] ? 1 : 0;
      });
    },
    exportData(params) {
      const type = 'text/csv;charset=UTF-8';
      const wscols = [];
      for (let i = 0; i < params.header.length; i++) {  // columns length added
        wscols.push({wch: 25});
      }
      const customFields = params.customFields;
      const worksheet = XLSX.utils.json_to_sheet(params.exportData);

      for (const cell in worksheet) {
        if (worksheet.hasOwnProperty(cell)) {
          // Get current column name
          const col = cell.split(/([0-9]+)/)[0];

          // Add cell styles to the required fields
          if (customFields.requiredFields[worksheet[cell].v]) {
            worksheet[cell].s = {
              fill: {
                fgColor: {
                  rgb: 'F5C6CB',
                },
              },
            };
          }
          // Add cell format for date and number fields
          if (customFields.dateFields[worksheet[cell].v]) {
            this.getExcelColumns(col, worksheet, 'yyyy/mm/dd');
          }
          if (customFields.dateTimeFields[worksheet[cell].v]) {
            this.getExcelColumns(col, worksheet, 'yyyy/mm/dd hh:mm:ss');
          }

          if (customFields.wholeNumberFields.separation[worksheet[cell].v]) {
            this.getExcelColumns(col, worksheet, '#,##0');
          }
          if (customFields.decimalFields.separation[worksheet[cell].v]) {
            const zeroCount = this.displayToDecimal(customFields.decimalFields.separation[worksheet[cell].v]);
            this.getExcelColumns(col, worksheet, `#,##0.${zeroCount}`);
          }

          if (customFields.wholeNumberFields.nonSeparation[worksheet[cell].v]) {
            this.getExcelColumns(col, worksheet, '0');
          }
          if (customFields.decimalFields.nonSeparation[worksheet[cell].v]) {
            const zeroCount = this.displayToDecimal(customFields.decimalFields.nonSeparation[worksheet[cell].v]);
            this.getExcelColumns(col, worksheet, `0.${zeroCount}`);
          }
        }
      }
      worksheet['!cols'] = wscols;
      const workbook = {Sheets: {'data': worksheet}, SheetNames: ['data']};
      const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
      const data = new Blob([excelBuffer], {
        type: type,
      });
      FileSaver.saveAs(data, `${this.table.schema.displayName} (History).xlsx`);
    },
    getExcelColumns(col, ws, fmt) {
      const C = XLSX.utils.decode_col(col);
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let i = range.s.r + 1; i <= range.e.r; ++i) {
        const ref = XLSX.utils.encode_cell({r: i, c: C});
        if (!ws[ref]) continue;
        if (ws[ref].t !== 'n') continue;
        ws[ref].z = fmt;
      }
    },
    displayToDecimal(field) {
      const json = JSON.parse(field.dataTypeJSON);
      return '0'.repeat(json.DecimalsToDisplay);
    },
  },
  mounted() {
    this.getData();
  },
};
</script>

<style lang="scss" scoped>
.page-data-management {
  flex: 1;
  overflow: hidden;
}

.complex-table-wrapper {
  flex: 1;
  overflow: hidden;
}
</style>
